import { useEffect, useRef } from 'react';
import { cn } from '../../lib/cn.js';

/**
 * VOICE-001 — the orb at the centre of the live voice screen.
 *
 * Three translucent blobs, each a ring of points pushed in and out by layered
 * sine waves, drawn over one another so the overlap reads as one liquid
 * sphere. What the orb is doing is the screen's state, before any caption is
 * read: it swells with the voice while listening, settles to a slow breathe
 * when paused or waiting for the microphone, turns its layers against each
 * other while the note is being worked on, collapses into a check mark when the
 * draft is ready, and dims into the destructive hue when it failed.
 *
 * Colour comes from the theme, never from here. The accent is a runtime HSL
 * triplet on <html> (theme.ts writes `--primary` inline from the user's
 * choice), and the three layers take its hue, hue + 25 and hue − 30 — a family
 * rather than a rainbow, so a teal business gets a teal orb. The same goes for
 * light and dark: on a dark panel the layers add up like light (the prototype's
 * look); on a light one they are laid over each other as paint instead (see
 * `Look` below).
 *
 * `level` is pulled once a frame, not passed as a value: the microphone's
 * loudness changes sixty times a second, and only the canvas needs to know.
 *
 * With `prefers-reduced-motion` there is no animation loop at all: one still
 * frame for each state, redrawn when the state or the theme changes.
 */
export type VoiceOrbMode = 'idle' | 'listening' | 'paused' | 'busy' | 'ready' | 'failed';

interface Palette {
  hue: number;
  sat: number;
  dangerHue: number;
  dangerSat: number;
  dark: boolean;
}

/**
 * How bright a hue is to the eye at full colour, 0 to 1 — yellow and cyan near
 * the top, blue and violet near the bottom (Rec. 709 weights over the pure
 * hue). On a dark panel the layers are added together, and three violet layers
 * add up to a glow while three teal or gold ones add up to a white-hot blot:
 * the bright hues are taken down before they are added, by this much.
 */
export function hueBrightness(hue: number): number {
  const channel = (n: number) => {
    const k = (n + hue / 30) % 12;
    const c = 0.5 - 0.5 * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(8) + 0.0722 * channel(4);
}

/** The backing store is square and fixed; CSS sizes the element. */
const SIZE = 640;
const C = SIZE / 2;
const POINTS = 96;

function triplet(value: string, fallback: [number, number]): [number, number] {
  const match = value.trim().match(/^([\d.]+)\s+([\d.]+)%/);
  return match ? [Number(match[1]), Number(match[2])] : fallback;
}

function readPalette(): Palette {
  const root = document.documentElement;
  const css = getComputedStyle(root);
  const [hue, sat] = triplet(css.getPropertyValue('--primary'), [265, 52]);
  const [dangerHue, dangerSat] = triplet(css.getPropertyValue('--destructive'), [0, 72]);
  // The accents are chosen to carry white text, so they sit around half
  // saturation; an orb at that saturation looks like a stain. Lifted, but in
  // proportion — a slate or a black accent stays a quiet, nearly grey orb
  // instead of being pushed into a colour the business never picked.
  return { hue, sat: Math.min(88, sat * 1.65), dangerHue, dangerSat: Math.min(70, dangerSat * 0.85), dark: root.classList.contains('dark') };
}

/**
 * How the layers are mixed, per ground. On dark the layers add up like light,
 * the prototype's look, and the overlap glows. On light, adding light only
 * pushes the overlap toward white — and for a green-blue accent, toward neon,
 * since two of the three channels saturate first — so there the layers are
 * laid over each other as paint, translucent, from a tint to a deep shade,
 * which keeps the accent's own colour at the centre.
 */
interface Look {
  blend: GlobalCompositeOperation;
  maxSat: number;
  tint: [number, number, number];
  deep: [number, number, number];
  alpha: [number, number];
  glow: number;
  shine: number;
}

const DARK_LOOK: Look = {
  blend: 'lighter',
  maxSat: 88,
  tint: [52, 56, 62],
  deep: [32, 40, 46],
  alpha: [0.46, 0.4],
  glow: 0.35,
  shine: 0.32,
};

const LIGHT_LOOK: Look = {
  blend: 'source-over',
  maxSat: 78,
  tint: [72, 66, 60],
  deep: [42, 36, 46],
  alpha: [0.5, 0.62],
  glow: 0.26,
  shine: 0.5,
};

interface Motion {
  level: number;
  morph: number;
}

function blobPath(ctx: CanvasRenderingContext2D, r: number, amp: number, speed: number, phase: number, t: number) {
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < POINTS; i++) {
    const a = (i / POINTS) * Math.PI * 2;
    const n =
      Math.sin(a * 3 + t * speed + phase) * 0.5 +
      Math.sin(a * 5 - t * speed * 1.3 + phase * 2) * 0.3 +
      Math.sin(a * 2 + t * speed * 0.7) * 0.2;
    const rr = r * (1 + amp * n);
    pts.push([C + Math.cos(a) * rr, C + Math.sin(a) * rr]);
  }
  ctx.beginPath();
  for (let i = 0; i < POINTS; i++) {
    const p = pts[i]!;
    const q = pts[(i + 1) % POINTS]!;
    const mx = (p[0] + q[0]) / 2;
    const my = (p[1] + q[1]) / 2;
    if (i === 0) ctx.moveTo(mx, my);
    else ctx.quadraticCurveTo(p[0], p[1], mx, my);
  }
  ctx.closePath();
}

function draw(ctx: CanvasRenderingContext2D, mode: VoiceOrbMode, t: number, m: Motion, p: Palette) {
  ctx.clearRect(0, 0, SIZE, SIZE);
  const busy = mode === 'busy';
  const failed = mode === 'failed';
  const listening = mode === 'listening';
  const resting = mode === 'idle' || mode === 'paused';
  const level = listening ? m.level : 0;

  const base = SIZE * 0.29 * (1 - m.morph * 0.3) * (failed ? 0.86 : 1);
  // Resting breathes slower and deeper, so a pause looks like a held breath
  // rather than the same orb with the sound turned off.
  const breathe = resting ? Math.sin(t * 0.9) * 0.035 : Math.sin(t * 1.6) * 0.02;
  const hue = failed ? p.dangerHue : p.hue;
  const sat = failed ? p.dangerSat : p.sat;
  const hues = failed ? [hue - 10, hue - 28, hue + 6] : [hue, hue + 25, hue - 30];
  const look = p.dark ? DARK_LOOK : LIGHT_LOOK;
  const layerSat = Math.min(sat, look.maxSat);
  // A grey accent has no hue to be bright or dull; it is as bright as white
  // in proportion to how grey it is, and is taken down like the bright hues.
  const glare = hueBrightness(hue) + (1 - hueBrightness(hue)) * (1 - layerSat / 100) ** 2;
  const dim = p.dark ? 1 - (0.55 * Math.max(0, glare - 0.08)) / 0.85 : 1;

  ctx.save();
  ctx.globalAlpha = failed ? 0.5 : 1;
  ctx.globalCompositeOperation = look.blend;

  const glow = ctx.createRadialGradient(C, C, base * 0.5, C, C, base * 1.68);
  glow.addColorStop(0, `hsla(${hues[0]},${layerSat}%,${60 * dim}%,${look.glow + level * 0.3})`);
  glow.addColorStop(1, `hsla(${hues[0]},${layerSat}%,60%,0)`);
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, SIZE, SIZE);

  for (let k = 0; k < 3; k++) {
    const amp = (listening ? 0.05 + level * 0.22 : busy ? 0.07 : resting ? 0.025 : 0.03) * (1 - m.morph);
    const speed = busy ? 2.4 + k : resting ? 0.5 + k * 0.2 : 1 + k * 0.4 + level * 2;
    const r = base * (1 + breathe + level * 0.12) * (1 - k * 0.07);
    ctx.save();
    if (busy) {
      ctx.translate(C, C);
      ctx.rotate(t * (0.6 + k * 0.25) * (k % 2 ? -1 : 1));
      ctx.translate(-C, -C);
    }
    blobPath(ctx, r, amp, speed, k * 2.1, t);
    const fill = ctx.createLinearGradient(C - r, C - r, C + r, C + r);
    fill.addColorStop(0, `hsla(${hues[k]},${layerSat}%,${look.tint[k]! * dim}%,${look.alpha[0]})`);
    fill.addColorStop(1, `hsla(${hues[(k + 1) % 3]},${layerSat * 0.92}%,${look.deep[k]! * dim}%,${look.alpha[1]})`);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.restore();
  }

  ctx.globalCompositeOperation = 'source-over';
  // Kept inside the sphere: a highlight that reached past the edge painted a
  // pale crescent on the dark panel, like a second, misplaced orb.
  const hx = C - base * 0.3;
  const hy = C - base * 0.34;
  const highlight = ctx.createRadialGradient(hx, hy, 0, hx, hy, base * 0.56);
  highlight.addColorStop(0, `rgba(255,255,255,${look.shine})`);
  highlight.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = highlight;
  ctx.beginPath();
  ctx.arc(C, C, base * 1.05, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Ready: a check draws itself inside the settled orb, the first stroke
  // before the second, the way a hand would.
  if (m.morph > 0.05) {
    ctx.save();
    ctx.strokeStyle = `rgba(255,255,255,${Math.min(1, m.morph)})`;
    ctx.lineWidth = SIZE * 0.024;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const s = base * 0.62;
    const progress = Math.min(1, m.morph * 1.2);
    const a: [number, number] = [C - s * 0.5, C + s * 0.02];
    const b: [number, number] = [C - s * 0.1, C + s * 0.4];
    const c: [number, number] = [C + s * 0.55, C - s * 0.35];
    ctx.beginPath();
    ctx.moveTo(a[0], a[1]);
    if (progress < 0.4) {
      const q = progress / 0.4;
      ctx.lineTo(a[0] + (b[0] - a[0]) * q, a[1] + (b[1] - a[1]) * q);
    } else {
      const q = (progress - 0.4) / 0.6;
      ctx.lineTo(b[0], b[1]);
      ctx.lineTo(b[0] + (c[0] - b[0]) * q, b[1] + (c[1] - b[1]) * q);
    }
    ctx.stroke();
    ctx.restore();
  }
}

export function VoiceOrb({
  mode,
  level,
  className,
}: {
  mode: VoiceOrbMode;
  level: () => number;
  className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // The loop reads these each frame instead of restarting on every change: a
  // restart would reset time, and the orb would jump at each new caption.
  const modeRef = useRef(mode);
  const levelRef = useRef(level);
  const redrawRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    modeRef.current = mode;
    levelRef.current = level;
    redrawRef.current?.();
  }, [mode, level]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;

    let palette = readPalette();
    const motion: Motion = { level: 0, morph: modeRef.current === 'ready' ? 1 : 0 };
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    const still = () => {
      draw(ctx, modeRef.current, 0, { level: 0, morph: modeRef.current === 'ready' ? 1 : 0 }, palette);
    };

    // The accent and the mode can change while the screen is open — the
    // account screen is a tap away on a desktop, and the system's own dark
    // switch flips the class under us. Both land as an attribute on <html>.
    const observer = new MutationObserver(() => {
      palette = readPalette();
      if (reduced) still();
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });

    if (reduced) {
      redrawRef.current = still;
      still();
      return () => {
        observer.disconnect();
        redrawRef.current = null;
      };
    }

    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const current = modeRef.current;
      const target = current === 'listening' ? levelRef.current() : 0;
      // Eased toward the target rather than set: raw microphone level jumps
      // every frame, and an orb that jumped with it would flicker, not breathe.
      motion.level += (target - motion.level) * 0.18;
      motion.morph += ((current === 'ready' ? 1 : 0) - motion.morph) * 0.08;
      draw(ctx, current, (now - start) / 1000, motion, palette);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} width={SIZE} height={SIZE} aria-hidden="true" className={cn('block', className)} />;
}
